import { ServerApiMapper } from '~commonInfra/utils/ServerModelHelper';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { GetRunFlowTaskSubscriptionFlowRequest,GetRunFlowTaskSubscriptionFlowResult } from '~src/flow/serverApi/requests/GetRunFlowTaskSubscriptionFlowRequest';
import { IActionResource } from '~src/httpClient/resources/Resource';
import WebHttpClient from '~src/httpClient/WebHttpClient';
import { SelfServiceSchedule } from '~src/schedule/serverApi/types/SelfServiceSchedule';
import { selectSpaceInfo } from '~src/spacesPlatform/selectors/SpaceInfoSelectors';
import { isServerTasksSpace, SpaceInfo } from '~src/spacesPlatform/state/SpaceInfo';

import { WebClientListResourceFetcher } from '../../httpClient/fetchers/resourceFetchers/WebClientListResourceFetcher';
import { ResourceQuery } from '../../httpClient/resources/ResourceQuery';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { RequestHelper } from '../../httpClient/utils/RequestHelper';
import { ResultHelper } from '../../httpClient/utils/ResultHelper';
import { WebHttpClientWithErrorToastInstance } from '../../httpClient/WebHttpClientWithErrorToast';
import { RunFlowTaskInfo } from '../models/RunFlowTaskInfo';
import { CreateRunFlowTaskRequest } from '../serverApi/requests/CreateRunFlowTaskRequest';
import { CreateRunFlowTaskSelfServiceSchedulesRequest } from '../serverApi/requests/CreateRunFlowTaskSelfServiceSchedulesRequest';
import { DeleteRunFlowTasksRequest } from '../serverApi/requests/DeleteRunFlowTasksRequest';
import { GetFlowActionsRequest } from '../serverApi/requests/GetFlowActionsRequest';
import { GetRunFlowTasksForServerRequest,GetRunFlowTasksRequest } from '../serverApi/requests/GetRunFlowTasksRequest';
import { ResumeRunFlowTasksRequest } from '../serverApi/requests/ResumeRunFlowTasksRequest';
import { RunFlowTasksRequest } from '../serverApi/requests/RunFlowTasksRequest';
import { SetRunFlowTaskOutputStepsRequest } from '../serverApi/requests/SetRunFlowTaskOutputStepsRequest';
import { SetRunFlowTasksPriorityRequest } from '../serverApi/requests/SetRunFlowTasksPriorityRequest';
import { SetRunFlowTasksScheduleRequest } from '../serverApi/requests/SetRunFlowTasksScheduleRequest';
import { SetRunFlowTasksSelfServiceScheduleRequest } from '../serverApi/requests/SetRunFlowTasksSelfServiceScheduleRequest';
import { FlowAllowedActions } from '../serverApi/types/FlowAllowedActions';
import { IFlowRunSpecOutputStepViewModel } from '../serverApi/types/IFlowRunSpecOutputStepViewModel';
import { SubscriptionFlow } from '../serverApi/types/SubscriptionFlow';
import { FlowParameterParams } from '../types/FlowParameters';
import { isFlowSelfServiceSchedulesEnabled } from '../utils/FlowSelfServiceSchedulesUtils';

export class RunFlowTasksResource implements IActionResource<FlowAllowedActions> {

  private siteFetcher = new WebClientListResourceFetcher(GetRunFlowTasksRequest, ServerApiMapper.runFlowTasks);
  private serverFetcher = new WebClientListResourceFetcher(GetRunFlowTasksForServerRequest, ServerApiMapper.runFlowTasks);

  public MAX_PRIORITY = 100;
  public MIN_PRIORITY = 1;

  public all(): ResourceQuery<RunFlowTaskInfo> {
    const appState = getAppStore().getState();
    const spaceInfo: SpaceInfo = selectSpaceInfo(appState);

    if (isServerTasksSpace(spaceInfo)) {
      return new ResourceQuery(this.serverFetcher);
    }

    return new ResourceQuery(this.siteFetcher);
  }

  public actions(flowId: string): Promise<FlowAllowedActions> {
    return WebHttpClient.sendRequest(new GetFlowActionsRequest(flowId));
  }

  public del(ids: string[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequests(
      RequestHelper.buildRequests(ids, (idsBlock) => new DeleteRunFlowTasksRequest(idsBlock)),
      ResultHelper.combineResults);
  }

  public run(ids: string[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequests(
      RequestHelper.buildRequests(ids, (idsBlock) => new RunFlowTasksRequest(idsBlock)),
      ResultHelper.combineResults);
  }

  public setPriority(ids: string[], priority: number): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequests(
      RequestHelper.buildRequests(ids, (idsBlock) => new SetRunFlowTasksPriorityRequest(idsBlock, priority)),
      ResultHelper.combineResults);
  }

  public setSchedule(ids: string[], scheduleId: string, selfServiceSchedule: SelfServiceSchedule): Promise<IResult> {
    if(isFlowSelfServiceSchedulesEnabled()) {
      return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
        RequestHelper.buildRequests(ids, (idsBlock) => new SetRunFlowTasksSelfServiceScheduleRequest({ids: idsBlock, schedule: selfServiceSchedule})),
        ResultHelper.combineResults
      );
    }

    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new SetRunFlowTasksScheduleRequest(idsBlock, scheduleId)),
      ResultHelper.combineResults
    );
  }

  public getSubscriptionFlow(taskId: string): Promise<GetRunFlowTaskSubscriptionFlowResult> {
    return WebHttpClient.sendRequest(new GetRunFlowTaskSubscriptionFlowRequest(taskId))
      .then((result: GetRunFlowTaskSubscriptionFlowResult) => result);
  }

  public setOutputSteps(
    taskId: string,
    runAllOutputs: boolean,
    isRunAllIncremental: boolean,
    ids: string[],
    flowRunSpecOutputStepParams: IFlowRunSpecOutputStepViewModel[],
    parameterParams?: FlowParameterParams[],
  ): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(
      new SetRunFlowTaskOutputStepsRequest({
        id: taskId,
        runAllOutputs: runAllOutputs,
        isRunAllIncremental,
        flowOutputStepIds: ids,
        flowRunSpecOutputStepParams,
        parameterParams: parameterParams,
      })
    );
  }

  public resume(ids: string[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequests(
      RequestHelper.buildRequests(ids, (idsBlock) => new ResumeRunFlowTasksRequest(idsBlock)),
      ResultHelper.combineResults);
  }

  public create(
    flowId: string,
    scheduleId: string,
    schedule: SelfServiceSchedule,
    runAllOutputs: boolean,
    isRunAllIncremental: boolean,
    flowOutputStepIds: string[],
    flowRunSpecOutputStepParams: IFlowRunSpecOutputStepViewModel[],
    sendEmailWhenDone: boolean,
    subscriptionFlow?: SubscriptionFlow,
    parameterParams?: FlowParameterParams[]): Promise<IResult> {
    const params = {
      schedule,
      flowId,
      runAllOutputs,
      isRunAllIncremental,
      flowOutputStepIds,
      flowRunSpecOutputStepParams,
      sendEmailWhenDone,
      subscriptionFlow: subscriptionFlow !== null && sendEmailWhenDone ? subscriptionFlow : null,
      parameterParams,
    };

    if(isFlowSelfServiceSchedulesEnabled()) {
      return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(
        new CreateRunFlowTaskSelfServiceSchedulesRequest({
          ...params,
        }));
    }

    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(
      new CreateRunFlowTaskRequest({
        ...params,
        scheduleId,
      }));
  }
}

export const RunFlowTasks = new RunFlowTasksResource();
